'use client';
import useTranslation from "../../context/Translation/useTranslation";
import StarIcon from '@repo/ui/src/components/Icons/StarIcon';
import { classNames } from '@repo/ui/src/utils/classNames';

interface RatingProps {
  value: number | null | undefined;
  hideValue?: boolean;
  ratingText?: string;
  outOfText?: string;
  reviewCount?: number;
  className?: string;
  dark?: boolean;
  showCountInParentheses?: boolean;
}

const RatingStars = ({
  value,
  hideValue,
  ratingText = 'Rating',
  outOfText = 'out of',
  reviewCount,
  className,
  dark = false,
  showCountInParentheses = false,
}: RatingProps) => {
  const { t } = useTranslation();
  const _value = value ?? 0;
  const decimalPercent = Math.floor((_value % 1) * 100);
  const roundedValue = _value.toFixed(1);
  const floorValue = Math.floor(_value);
  const showReviewCount = reviewCount != null;
  const hasNoReviews = (showReviewCount && reviewCount === 0) || value == null;
  const _hideValue = hideValue || hasNoReviews;

  const getFillPercent = (index: number) => {
    if (hasNoReviews || index < floorValue) {
      return 100;
    }
    if (index === floorValue) {
      return decimalPercent;
    }
    return 0;
  };

  return (
    <div
      className={classNames('tw-flex tw-gap-2 tw-items-center tw-align-middle', className)}
      role="img"
      aria-label={`${ratingText}: ${
        hasNoReviews ? 'no reviews' : `${roundedValue} ${outOfText} 5`
      }`}
      data-testid="rating-stars"
    >
      <div
        className={classNames(
          'tw-flex tw-items-center',
          hasNoReviews ? 'tw-text-[#949494]' : 'tw-text-brand-400',
        )}
      >
        {[...Array(5)].map((x, i) => (
          <StarIcon key={i} fillPercent={getFillPercent(i)} className="tw-w-6" />
        ))}
      </div>
      {!_hideValue && (
        <span
          className={classNames('tra-text-web-body-l', dark && 'tw-text-brand-300')}
          data-testid="rating-value"
        >
          {roundedValue}
        </span>
      )}
      {!_hideValue && showReviewCount && (
        <span className={classNames('tw-self-stretch tw-border-l-2 tw-border-warm-400')} />
      )}
      {showReviewCount && (
        <div className="tw-border-warm-400" data-testid="rating-count">
          <p className={classNames('tra-text-web-body-l', dark && 'tw-text-brand-300')}>
            {showCountInParentheses
              ? `(${reviewCount ?? 0})`
              : `${reviewCount ?? 0} ${t('genericContent.reviews', undefined, { default: 'Reviews' })}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default RatingStars;
